import { useCallback, useMemo, useState } from 'react'
import { Form } from 'react-final-form'
import { EnvelopeIcon } from '@heroicons/react/24/outline'
import { Venue } from 'types/model'
import { AdmissionGroup } from 'types/model/admission-group'

import { Button } from '@elements/button'
import { ErrorNote } from '@elements/error-note'
import { TextField } from '@components/form-field/text'
import { DateTextField } from '@components/form-field/date-text'
import { PhoneField } from '@components/form-field/phone'
import { CheckboxField } from '@components/form-field/checkbox'

import { createValidator, validations } from '@lib/validation'
import { useSession } from '@hooks/session'
import {
  getDateInputPlaceholderByVenueDateDisplayFormat,
  getLuxonDateFormatByVenueDateDisplayFormat,
  isoToUTC,
  withDateFormat,
} from '@utils/date-time'
import { countryCodeSafePhone, getId } from '@utils/strings'
import { bookingApi } from '../data'

export interface GuestlistFormProps {
  venue: Venue
  date: string
  afterSubmit: (vals: AdmissionGroup) => void
  attributionId?: string | null
  affiliateId?: string | null
}

type GuestlistFormValues = Partial<{
  name: string
  email: string
  phone: string
  birthday: string
  guestsExpected: number
  notes: string | null
  customerMarketingOptIn: boolean
}>

function useGuestlistValidator({ venue }: { venue: Venue }) {
  const guestlistMax = venue.config.guestlistMax ?? 1000
  return useMemo(
    () =>
      createValidator({
        name: validations.string().required('Please provide your full name'),
        email: validations
          .string()
          .required('Please provide your email address')
          .email(),
        phone: validations
          .string()
          .required('Please provide your phone number'),
        birthday: validations.string().required('Please provide your birthday'),
        guestsExpected: validations
          .number()
          .required('Please provide the number of guests (including you)')
          .min(1)
          .max(
            guestlistMax,
            `Can not include more than ${guestlistMax} guests`
          ),
        notes: validations.string().nullable(),
      }),
    [guestlistMax]
  )
}

export function GuestlistForm({
  venue,
  afterSubmit,
  date,
  attributionId,
  affiliateId,
}: GuestlistFormProps) {
  const session = useSession()
  const [errorMessage, setErrorMessage] = useState<string>()

  const initialValues = useMemo(
    () => ({
      name: session.user ? session.user.name : '',
      email: session.user ? session.user.email : '',
      phone: session.user ? countryCodeSafePhone(session.user.phone) : '',
      birthday: session.user?.birthday
        ? new Date(session.user.birthday).toISOString()
        : undefined,
      customerMarketingOptIn: session.user
        ? session.user.marketingOptIn
        : false,
    }),
    [session.user]
  )

  const guestlistValidator = useGuestlistValidator({ venue })
  const dateFormat = getLuxonDateFormatByVenueDateDisplayFormat(
    venue.dateDisplayFormat
  )
  const datePlaceholder = getDateInputPlaceholderByVenueDateDisplayFormat(
    venue.dateDisplayFormat
  )

  const submit = useCallback(
    async (vals: GuestlistFormValues) => {
      const {
        name,
        email,
        phone,
        birthday,
        guestsExpected,
        notes,
        customerMarketingOptIn,
      } = vals
      try {
        if (!venue) throw new Error('Venue not found')

        const birthDt = new Date(birthday as string)
        const birthdayDay = birthDt.getDate()
        const birthdayMonth = birthDt.getMonth()
        const birthdayYear = birthDt.getFullYear()

        const response = await bookingApi.requestReservation({
          attributionId,
          affiliateId,
          date: isoToUTC(date),
          birthday: withDateFormat(birthDt, dateFormat),
          birthdayDay,
          birthdayMonth,
          birthdayYear,
          customerMarketingOptIn,
          name,
          email,
          phone,
          guestsExpected: Number(guestsExpected),
          guestsExpectedUnisex: Number(guestsExpected),
          guestsExpectedFemale: null,
          guestsExpectedMale: null,
          notes: notes || null,
          type: 'GUEST_LIST',
          venueId: getId(venue),
        })

        setErrorMessage(undefined)
        afterSubmit?.(response)
      } catch (e) {
        if (e instanceof Error) setErrorMessage(e.message)
      }
    },
    [affiliateId, afterSubmit, attributionId, date, venue, dateFormat]
  )

  return (
    <Form
      onSubmit={submit}
      validate={guestlistValidator}
      initialValues={initialValues}
    >
      {({ handleSubmit, submitting }) => (
        <form onSubmit={handleSubmit} className='contents'>
          <div className='space-y-6'>
            <div className='space-y-3'>
              <TextField
                name='name'
                label='Full Name'
                autoComplete='name'
                placeholder='Please enter your name'
              />
              <TextField
                name='email'
                label='Email'
                autoComplete='email'
                placeholder='johndoe@email.com'
                type='email'
              />
              <div className='flex flex-col items-start justify-center w-full space-y-3 xs:space-y-0 xs:space-x-3 xs:flex-row'>
                <PhoneField name='phone' label='Phone' />
                <DateTextField
                  name='birthday'
                  label='Birthday'
                  placeholder={datePlaceholder}
                  initialValue={initialValues.birthday}
                />
              </div>
              <TextField
                pattern='\d*'
                type='number'
                label='Guest Count'
                name='guestsExpected'
                placeholder='# of guests arriving'
              />
              <TextField
                as='textarea'
                name='notes'
                label='Questions and Comments'
                placeholder='Anything additional here'
              />
              {venue?.showMarketingOptIn && (
                <CheckboxField name='customerMarketingOptIn'>
                  <span className='text-base sm:text-md'>
                    {venue.marketingOptInText ??
                      `Yes I'd like to receive messages and notifications from ${venue.name}`}
                  </span>
                </CheckboxField>
              )}
            </div>
            <Button.Submit
              rightIcon={EnvelopeIcon}
              className='w-full'
              loading={submitting}
            >
              Submit
            </Button.Submit>
            <ErrorNote>{errorMessage}</ErrorNote>
          </div>
        </form>
      )}
    </Form>
  )
}
